import { makeApi, URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { toasterState } from "Components/common/toaster";
import { emailPreviewContentPopupState } from "Components/PreviewPopup/EmailPreviewContentPopup";

/*
 * Drives the shared rich reply editor (EmailComposeEditor) INLINE inside the Inbox V2
 * thread instead of the modal. It reproduces the REPLY path of
 * EmailPreviewContentPopup.getReplyAndForwardDetails + handleReplyForwardResponseData:
 * fetch the threaded reply content, then populate emailPreviewContentPopupState so the
 * editor mounts. `showPopup` is left false so the global modal never appears.
 *
 * isEnableReplyFrwd:true + taskId make EmailComposeEditor.sendEmail() hit
 * URLS.sendReplyEmail (a reply), not a fresh mail.
 */

// ui mode -> the same emailType constants EmailComposeEditor.sendEmail expects.
const EMAIL_TYPE = {
  reply: "REPLY",
  replyAll: "REPLY_ALL",
  forward: "FORWARD"
};

// Mirrors EmailPreviewContentPopup.handleReplyForwardResponseData: one fetch returns both
// reply and forward variants; the mode picks subject/content, and only REPLY drops Cc/Bcc.
const buildReplyEditorInput = async (data = {}, mode = "reply") => {
  const {
    ccEmail = [],
    bccEmail = [],
    fromEmail = "",
    replyContent = "",
    replySubject = "",
    forwardContent = "",
    forwardSubject = "",
    toEmail = [],
    threadId = ""
  } = data || {};

  const emailType = EMAIL_TYPE[mode] || "REPLY";
  const isReply = emailType === "REPLY";
  const isForward = emailType === "FORWARD";

  const newContent = Utils.addEmptySpaceAboveThreadEmail(
    isForward ? forwardContent : replyContent
  );
  const emailStatusObj = await Utils.validateEmailAddressStatus(
    Utils.getEmailList({ to: toEmail, cc: ccEmail, bcc: bccEmail })
  );
  if (typeof emailStatusObj !== "object") {
    toasterState.setToastMsg(
      "Something went wrong. Please try again later",
      "fail"
    );
    return false;
  }

  const validated = Utils.addEmailStatus(
    { to: toEmail || [], cc: ccEmail || [], bcc: bccEmail || [] },
    emailStatusObj
  );
  const popupValues = emailPreviewContentPopupState.popupValues || {};
  const emailDataObj = {
    ...(popupValues.emailData || {}),
    from: fromEmail,
    to: validated.to,
    cc: isReply ? [] : validated.cc,
    bcc: isReply ? [] : validated.bcc,
    threadId,
    subject: isForward ? forwardSubject : replySubject,
    content: Utils.splitAndJoinDomWithoutSpace(newContent)
  };
  const localStateObj = {
    ...(emailPreviewContentPopupState.editorInputValue?.localState || {}),
    showCcInputAddress: isReply ? false : ccEmail?.length > 0,
    showBccInputAddress: isReply ? false : bccEmail?.length > 0,
    editorInsertCounter: 1
  };
  return {
    emailType,
    ...popupValues,
    emailData: emailDataObj,
    localState: localStateObj,
    replyForwardData: data
  };
};

// Returns true once the editor is populated and shown, false on any early-out/error, so
// the caller can drop its "opening" state instead of spinning forever.
export const openInlineReply = async (taskId = "", options = {}) => {
  const { emailData = {}, mode = "reply" } = options || {};
  if (!taskId) {
    toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
    return false;
  }
  if (!Utils.isAccountConnected()) {
    toasterState.setToastMsg("Please connect an inbox to send emails.", "fail");
    return false;
  }

  emailPreviewContentPopupState.setPopupValues({
    taskId,
    isEnableReplyFrwd: true,
    eventType: "REPLIED",
    group: "EMAIL",
    pageType: "outboxReply",
    emailData
  });
  emailPreviewContentPopupState.setEditorLoading(true);

  let opened = false;
  try {
    const res = await makeApi({
      url: URLS.getReplyEmailContent,
      method: "POST",
      data: { taskId }
    });
    if (res?.data?.statusCode === 200) {
      const editorInput = await buildReplyEditorInput(res.data, mode);
      if (editorInput) {
        emailPreviewContentPopupState.setEditorInputValue(editorInput);
        emailPreviewContentPopupState.setshowEditorComposePopup(true);
        opened = true;
      }
    } else if (res?.data?.statusCode === 407) {
      const errorInbox = res?.data?.errorInbox || "";
      toasterState.setToastMsg(
        errorInbox?.length
          ? `Oops! Looks like the inbox ${errorInbox} is not connected. Please connect this inbox to send the email.`
          : "Please connect an inbox to send emails.",
        "fail"
      );
    } else if (res?.data?.statusCode === 401) {
      toasterState.setToastMsg(
        "Oops! You don't have access to send emails to this contact.",
        "fail"
      );
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
  } catch (err) {
    console.error("Error opening inline reply editor", err);
    toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
  }
  emailPreviewContentPopupState.setEditorLoading(false);
  return opened;
};

// Reply editor targets our thread only while it's open for our task.
export const isInlineReplyOpen = () =>
  emailPreviewContentPopupState.showEditorComposePopup &&
  !emailPreviewContentPopupState.showPopup;

export const closeInlineReply = () => {
  emailPreviewContentPopupState.setResetPopup();
};
